import type {
  BroadcastContent,
  ConnectingLineDelayByStationProperties,
  ConnectingLineDelayProperties,
  DelayBroadcastGranularity,
  DelayBroadcastPattern,
  SelfTrainDelayProperties,
} from '../api/types';

export type LocalizedText = Record<string, string>;
/**
 * 遅延放送パターン行の文面の種類。
 *
 * - `self` / `connecting` / `station`: 遅延分数を読み上げる文面 (粒度別)。
 * - `departure_forecast` (part1) / `station_departure_forecast` (part2):
 *   発車見込み案内 (SBI-5)。遅延分数ではなく発車見込み時刻と行先を読み上げる単一文面で、
 *   粒度によらず同じ (粒度の列も画面に出さない)。
 */
export type DelayTemplateKind =
  | 'self'
  | 'connecting'
  | 'station'
  | 'departure_forecast'
  | 'station_departure_forecast';

/**
 * 言語別入力欄 1 つぶんの `data-testid` を組み立てる (Issue #545)。
 *
 * ## なぜ規則を関数に集約するのか
 *
 * 「任意テキストの言語別文面が未入力でも保存できてしまう」問題を直すため、
 * バリデーション (`validateContentIssues`) は未入力の言語欄を `testId` で
 * 指し示す。UI 側 (`LocalizedTextFields`) とバリデーション側で文字列を
 * 別々に手打ちすると、片方を直したときに赤枠もスクロールも
 * **エラーにならず静かに効かなくなる**。そのため両者がこの関数を共有する。
 *
 * ⚠️ 本モジュール (`utils/`) に置くのは依存方向のため。`utils` から
 * `components` を import すると循環参照になる (逆は許容される)。
 *
 * @param testIdBase 入力欄グループの識別子 (`LocalizedTextFields` の
 *   `translateTestId`。例: 任意テキスト #1 なら `free-text-0`)
 * @param languageKey 対象の language_key (例: `en`)
 */
export function localizedTextFieldTestId(testIdBase: string, languageKey: string): string {
  return `localized-text-${testIdBase}-${languageKey}`;
}

/**
 * `route_nickname_i18n` (線区愛称の言語別名称) を保存 payload 用に正規化する
 * — Issue #579 (SBI-09)。
 *
 * サーバ側 (`_coerce_route_nickname_i18n`) と **同じ規則** に揃える:
 *
 * - 値の前後空白を除去する。
 * - 空文字に落ちたキーは **保存しない** (放送側で `ja` → `route_nickname` の順に
 *   フォールバックするため、空欄を保存する意味がない)。
 * - 有効な名称が 1 件も無ければ `undefined` を返す。呼び出し側はそのまま payload に
 *   入れることで **属性を作らない** (既存 item と bit-perfect 互換 = 後方互換)。
 *
 * ⚠️ フロントで正規化しておく理由: 空辞書 `{}` をそのまま送るとサーバ側でも
 * 未設定扱いになるため実害はないが、Export JSON に `route_nickname_i18n: {}` が
 * 混ざると「設定してあるように見えるゴミ」になる。入口で落とす。
 */
export function normalizeRouteNicknameI18n(
  value: LocalizedText | undefined | null,
): LocalizedText | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const normalized: LocalizedText = {};
  for (const [languageKey, nickname] of Object.entries(value)) {
    if (typeof nickname !== 'string') continue;
    const trimmed = nickname.trim();
    if (trimmed === '') continue;
    normalized[languageKey] = trimmed;
  }
  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

// ---------------------------------------------------------------------------
// SSML の整形式チェック (Issue #555 / 不具合 C)
// ---------------------------------------------------------------------------
//
// ## なぜ必要か
//
// 「日本語から翻訳」ボタンの機械翻訳が
//
//     <speak>… We apologize for any inconvenience this may have caused</speak>.
//                                                          ↑ 句点が </speak> の外
//
// のように **`</speak>` の外側にテキストがある SSML** を返すことがあった
// (Amazon Translate が文末の句読点を「文の区切り」として再配置する)。
// この形の SSML は Polly (TTS) でエラー / 意図しない読み上げになる。
//
// 翻訳結果は「下書き」として人の目に触れるが、**気付かず保存できてしまう** と
// 実放送で当該言語だけ壊れる。エラーにならず静かに壊れる経路なので、
// 保存前のバリデーションで弾いて赤枠 + エラーサマリで気付けるようにする。
//
// ⚠️ バックエンド (`lambda/control-panel-api/index.py` の `is_well_formed_ssml`) と
// **同じ規則** にすること。判定がズレると「画面では保存できるのに API が 400」
// (またはその逆) という分かりにくい失敗になる。
const SPEAK_OPEN_PATTERN = /<speak(?:\s[^>]*)?>/gi;
const SPEAK_CLOSE_PATTERN = /<\/speak\s*>/gi;
const SPEAK_WRAPPED_PATTERN = /^<speak(?:\s[^>]*)?>[\s\S]*<\/speak\s*>$/i;

/** 正規表現の一致件数を数える (`g` フラグ付き正規表現の状態を持ち越さない)。 */
function countMatches(text: string, pattern: RegExp): number {
  return text.match(new RegExp(pattern.source, pattern.flags))?.length ?? 0;
}

/**
 * SSML が整形式 (`<speak>` で始まり `</speak>` で終わる) か判定する (Issue #555)。
 *
 * `<speak>` を 1 つも含まないプレーンテキストは **true** を返す
 * (放送側が `build_ssml()` で自動ラップするため正しい入力である)。
 *
 * @param text 検査対象の文面 (`undefined` / `null` は検査不能として true)
 * @returns 整形式なら true。`</speak>` の外側にテキストがある / 閉じタグが無い /
 *   `<speak>` が複数ある場合は false。
 */
export function isWellFormedSsml(text: string | undefined | null): boolean {
  if (typeof text !== 'string') return true;
  const trimmed = text.trim();
  const openCount = countMatches(trimmed, SPEAK_OPEN_PATTERN);
  const closeCount = countMatches(trimmed, SPEAK_CLOSE_PATTERN);
  // プレーンテキスト。放送側で <speak> にラップされるため正しい。
  if (openCount === 0 && closeCount === 0) return true;
  // <speak> が無い / 複数ある (入れ子・連結) のは整形式でない。
  if (openCount !== 1 || closeCount !== 1) return false;
  return SPEAK_WRAPPED_PATTERN.test(trimmed);
}

/**
 * `</speak>` の外に出たテキストを `<speak>` の内側へ戻す (Issue #555)。
 *
 * 「日本語から翻訳」の訳文が壊れていた場合に、**下書きとして入力欄へ反映する
 * 時点で** 直しておくためのヘルパ。壊れた下書きをそのまま見せると、
 * 運用者が気付かず保存 → 400 で弾かれる、という遠回りになる。
 *
 * `<speak>` / `</speak>` がそれぞれ 1 個ある場合に限り修復し、それ以外
 * (プレーンテキスト / 入れ子 / 閉じタグ無し) はそのまま返す
 * (推測で書き換えると別の壊れ方を招くため)。
 *
 * ⚠️ バックエンド (`repair_ssml_wrapper`) と同じ規則にすること。
 *
 * @param text 修復対象の文面
 * @returns 整形式に修復した文面 (修復不要 / 不能ならそのまま)
 */
export function repairSsmlWrapper(text: string): string {
  if (typeof text !== 'string') return text;
  const trimmed = text.trim();
  if (countMatches(trimmed, SPEAK_OPEN_PATTERN) !== 1) return text;
  if (countMatches(trimmed, SPEAK_CLOSE_PATTERN) !== 1) return text;
  const open = new RegExp(SPEAK_OPEN_PATTERN.source, 'i').exec(trimmed);
  const close = new RegExp(SPEAK_CLOSE_PATTERN.source, 'i').exec(trimmed);
  if (!open || !close) return text;
  const openEnd = open.index + open[0].length;
  // </speak> が <speak> より前にある壊れ方は修復対象にしない。
  if (close.index < openEnd) return text;
  const prefix = trimmed.slice(0, open.index);
  const inner = trimmed.slice(openEnd, close.index);
  const suffix = trimmed.slice(close.index + close[0].length);
  if (prefix === '' && suffix === '') return trimmed;
  return `${open[0]}${prefix}${inner}${suffix}${close[0]}`;
}

/**
 * 言語別 (en / zh / ko) の **既定文面** を粒度ごとに組み立てる (Issue #556)。
 *
 * ⚠️ 戻り値は **placeholder (薄字) にのみ** 使うこと。値として入力欄へ流し込むと
 * 個別文面が定型文マスタより優先され、「定型文マスタを整備しても放送に反映されない」
 * (Issue #556) 状態に戻る。委譲の規約は Issue #527 と同じ「空欄 = マスタ委譲」。
 *
 * @param kind どのテンプレート層の文面か (`self` / `connecting` / `station`)
 * @param granularity 放送粒度
 */
export function localizedDelayDefaults(
  kind: DelayTemplateKind,
  granularity: DelayBroadcastGranularity,
): LocalizedText {
  const minutes = {
    '1分単位': '{delay_minutes_1}',
    '5分単位': '{delay_minutes_5}',
    '10分単位': '{delay_minutes_10}',
    大幅な遅延: '',
  }[granularity];
  const significant = granularity === '大幅な遅延';
  const approximate = granularity !== '1分単位';
  const english = significant
    ? 'is currently significantly delayed'
    : `is currently delayed by ${approximate ? 'approximately ' : ''}${minutes} minutes`;
  const chinese = significant ? '目前严重晚点' : `目前晚点${approximate ? '约' : ''}${minutes}分钟`;
  const korean = significant
    ? '현재 크게 지연되고 있습니다'
    : `현재 ${approximate ? '약 ' : ''}${minutes}분 지연되고 있습니다`;
  // 発車見込み案内は粒度によらず同じ文面 (遅延分数を読み上げない)。
  // バックエンド (`ssml_builder.py`) の基本 4 言語の既定文面と一致させること。
  if (kind === 'departure_forecast') {
    return { ...LOCALIZED_DEPARTURE_FORECAST };
  }
  if (kind === 'station_departure_forecast') {
    return { ...LOCALIZED_STATION_DEPARTURE_FORECAST };
  }
  if (kind === 'self') {
    return {
      en: `This train ${english}.`,
      zh: `您乘坐的列车${chinese}。`,
      ko: `지금 타고 계신 열차는 ${korean}.`,
    };
  }
  if (kind === 'station') {
    return {
      en: `service towards {direction_name} ${english}`,
      zh: `开往{direction_name}方向的列车${chinese}`,
      ko: `{direction_name} 방면 열차는 ${korean}`,
    };
  }
  return {
    en: `Connecting service on {route_name} {direction_name} from {from_station} ${english}.`,
    zh: `在{from_station}站换乘的{route_name}{direction_name}列车${chinese}。`,
    ko: `{from_station}역에서 갈아타실 {route_name} {direction_name} 열차는 ${korean}.`,
  };
}

export const LOCALIZED_STATION_INTRO: LocalizedText = {
  en: 'Transfer information at {transfer_station}. ',
  zh: '{transfer_station}站换乘信息。',
  ko: '{transfer_station}역 환승 안내입니다. ',
};

export const LOCALIZED_STATION_LINE: LocalizedText = {
  en: 'On {route_name}, {directions_text}.',
  zh: '{route_name}，{directions_text}。',
  ko: '{route_name}, {directions_text}.',
};

/**
 * 接続線遅延 (part2) の上限超過時の案内文 — 外国語の既定文面。
 *
 * `lambda/common/ssml_builder.py` の `CONNECTING_STATION_OVER_LIMIT_TEMPLATE_I18N`
 * (en / zh / ko) と文字列レベルで一致させること
 * (`tests/phrase_template_sync` が突き合わせる)。日本語は
 * `CONNECTING_STATION_OVER_LIMIT_DEFAULT_SSML` (`utils/broadcastContents.ts`)。
 * 外国語は機械翻訳案で、現場レビューで確定する。
 */
export const LOCALIZED_STATION_OVER_LIMIT: LocalizedText = {
  en:
    'Delays are occurring on the lines running through {transfer_station} Station. ' +
    'Please check station announcements and electronic display boards',
  zh: '途经{transfer_station}站的各线路出现延误。请留意车站广播及电子显示屏',
  ko:
    '{transfer_station}역을 운행하는 각 노선에 지연이 발생하고 있습니다. ' +
    '역 안내방송과 전광판을 확인해 주시기 바랍니다',
};

/**
 * 接続線の発車見込み案内 (part1) の完結文 — 外国語の既定文面。
 *
 * `lambda/common/ssml_builder.py` の `CONNECTING_DELAY_DEPARTURE_FORECAST_TEMPLATE_I18N`
 * (en / zh / ko) と文字列レベルで一致させること。日本語は
 * `CONNECTING_DEPARTURE_FORECAST_DEFAULT_SSML` (`utils/broadcastContents.ts`)。
 * 外国語は `{from_prefix}` (日本語の助詞込み) を使わず `{from_station}` を使う。
 */
export const LOCALIZED_DEPARTURE_FORECAST: LocalizedText = {
  en: 'Your connecting {route_name} {headsign} from {from_station} is expected to depart around {departure_time}.',
  zh: '您从{from_station}站换乘的{route_name}{headsign}预计{departure_time}左右发车。',
  ko: '{from_station}역에서 환승하시는 {route_name} {headsign}은 {departure_time}경 출발할 예정입니다.',
};

/**
 * 接続線の発車見込み案内 (part2) の方面部分文 — 外国語の既定文面。語尾は持たない
 * (接続線ブロック文 {@link LOCALIZED_STATION_DEPARTURE_FORECAST_LINE} が閉じる)。
 *
 * `CONNECTING_STATION_DEPARTURE_FORECAST_TEMPLATE_I18N` (en / zh / ko) と一致させること。
 */
export const LOCALIZED_STATION_DEPARTURE_FORECAST: LocalizedText = {
  en: '{headsign} around {departure_time}',
  zh: '{headsign}预计{departure_time}左右',
  ko: '{headsign}은 {departure_time}경',
};

/**
 * 接続線の発車見込み案内 (part2) の接続線ブロック文 — 外国語の既定文面。
 *
 * `CONNECTING_STATION_DEPARTURE_FORECAST_LINE_TEMPLATE_I18N` (en / zh / ko) と一致させること。
 */
export const LOCALIZED_STATION_DEPARTURE_FORECAST_LINE: LocalizedText = {
  en: 'the {route_name}: {directions_text}, expected to depart.',
  zh: '{route_name}：{directions_text}发车。',
  ko: '{route_name}: {directions_text} 출발할 예정입니다.',
};

/** `localizedDelayDefaults` の全パターン (kind × 粒度) を列挙するための組み合わせ。 */
const DELAY_TEMPLATE_KINDS: readonly DelayTemplateKind[] = [
  'self',
  'connecting',
  'station',
  'departure_forecast',
  'station_departure_forecast',
];
const DELAY_GRANULARITIES: readonly DelayBroadcastGranularity[] = [
  '1分単位',
  '5分単位',
  '10分単位',
  '大幅な遅延',
];

export function localizedConnectingSingleDefaults(mode: string): LocalizedText {
  if (mode === 'whole_line') {
    return {
      en: '<speak>Connecting services on {route_name} are currently delayed.</speak>',
      zh: '<speak>您要换乘的{route_name}目前晚点运行。</speak>',
      ko: '<speak>갈아타실 {route_name} 열차는 현재 지연 운행 중입니다.</speak>',
    };
  }
  return Object.fromEntries(
    Object.entries(localizedDelayDefaults('connecting', '1分単位')).map(([language, text]) => [
      language,
      `<speak>${text}</speak>`,
    ]),
  );
}

// ---------------------------------------------------------------------------
// 焼き込まれたコード内既定訳の剥がし (Issue #556)
// ---------------------------------------------------------------------------
//
// ## なぜ「剥がす」必要があるのか
//
// Issue #556 以前は、放送言語 (en / zh / ko) のチェックを入れた時点で
// `*_i18n` へ **コード内既定訳が値として自動投入** されていた。
// 個別文面は定型文マスタより優先されるため、
//
//   定型文マスタで英中韓の文面を整備しても、放送地点側で該当言語を
//   有効にした瞬間に無効化される (実測: 日本語以外の反映率 0%)
//
// という状態になっていた。日本語 (既定フィールド) は Issue #527 で
// 「空欄 = 定型文マスタへ委譲」に揃えてあり、言語別文面だけが取り残されていた形。
//
// そこで
//   1. UI は既定訳を **placeholder (薄字)** としてのみ表示する
//      (`LocalizedTextFields` の `placeholders`)
//   2. 既存データに焼き込まれた既定訳は **読み込み時に剥がす**
//      (= 再保存でキーが落ち、以後マスタが反映される)
// の 2 段構えで解消する。
//
// ⚠️ 運用者が手で書いた文面は既定訳と一致しないため温存される。
//    移行スクリプト (`scripts/unbake_default_ssml.py`) と同じ規則。

/**
 * 剥がして良い「コード内既定訳」の集合 (kind × 粒度 × 言語 + 単一テンプレート系)。
 *
 * ⚠️ 前後空白を除去して格納する。冒頭文の既定訳 (`LOCALIZED_STATION_INTRO`) は
 * 後続の line ブロック文と繋げるため **末尾に半角スペースを含む** 文面があり、
 * 生の文字列で突き合わせると照合に失敗して剥がし漏れる
 * (移行スクリプト `scripts/unbake_default_ssml.py` の `text.strip()` と同じ規則)。
 */
const KNOWN_LOCALIZED_DEFAULT_BODIES: ReadonlySet<string> = new Set<string>(
  [
    ...DELAY_TEMPLATE_KINDS.flatMap((kind) =>
      DELAY_GRANULARITIES.flatMap((granularity) =>
        Object.values(localizedDelayDefaults(kind, granularity)),
      ),
    ),
    ...Object.values(LOCALIZED_STATION_INTRO),
    ...Object.values(LOCALIZED_STATION_LINE),
    ...Object.values(LOCALIZED_STATION_OVER_LIMIT),
    ...Object.values(LOCALIZED_STATION_DEPARTURE_FORECAST_LINE),
    ...Object.values(localizedConnectingSingleDefaults('whole_line')),
    ...Object.values(localizedConnectingSingleDefaults('section_scheduled')),
  ].map((text) => text.trim()),
);

/**
 * 言語別文面 1 件が「剥がして良い」値か判定する (Issue #556)。
 *
 * 空文字 / 空白のみ (= 未入力) と、コード内既定訳と完全一致する値が対象。
 * それ以外 (運用者が書いた文面) は温存する。
 */
export function isRemovableLocalizedBody(value: unknown): boolean {
  if (typeof value !== 'string') return true;
  const trimmed = value.trim();
  return trimmed === '' || KNOWN_LOCALIZED_DEFAULT_BODIES.has(trimmed);
}

/**
 * `*_i18n` から「剥がして良い値」を取り除く (Issue #556)。
 *
 * @param localized 言語別文面の辞書
 * @param removeDefaults `true` ならコード内既定訳も剥がす。`false` なら
 *   空文字だけを剥がす (編集中に既定訳と一致した瞬間に消えるのを避けるため、
 *   フォーム更新経路ではこちらを使う)。
 * @returns 残った文面。1 件も残らなければ `undefined` (= 属性ごと省略する)
 */
function pruneLocalized(
  localized: LocalizedText | undefined,
  removeDefaults: boolean,
): LocalizedText | undefined {
  if (!localized || typeof localized !== 'object') return undefined;
  const next: LocalizedText = {};
  for (const [language, text] of Object.entries(localized)) {
    if (typeof text !== 'string') continue;
    if (text.trim() === '') continue;
    if (removeDefaults && isRemovableLocalizedBody(text)) continue;
    next[language] = text;
  }
  return Object.keys(next).length > 0 ? next : undefined;
}

/**
 * `<field>_i18n` を prune した結果でオブジェクトを置き換える (空なら属性を落とす)。
 *
 * 型を `object` 起点にしているのは、`properties` 系の型 (index signature を持たない
 * interface) をそのまま受けられるようにするため。返り値は入力と同じ型を保つ。
 */
function withPrunedField<T extends object>(source: T, field: string, removeDefaults: boolean): T {
  if (!(field in source)) return source;
  const record = source as unknown as Record<string, unknown>;
  const pruned = pruneLocalized(record[field] as LocalizedText | undefined, removeDefaults);
  const next: Record<string, unknown> = { ...record };
  if (pruned) next[field] = pruned;
  else delete next[field];
  return next as unknown as T;
}

function prunePatterns(
  patterns: DelayBroadcastPattern[] | undefined,
  removeDefaults: boolean,
): DelayBroadcastPattern[] | undefined {
  return patterns?.map((pattern) => withPrunedField(pattern, 'ssml_template_i18n', removeDefaults));
}

/**
 * コンテンツ 1 件の言語別文面を正規化する (Issue #556)。
 *
 * 「空欄 = 定型文マスタへ委譲」を言語別文面にも適用するための正規化。
 * 空文字のキーは常に落とし、`removeDefaults` が `true` のときは
 * 焼き込まれたコード内既定訳も落とす (既存データの移行)。
 *
 * @param content 対象コンテンツ
 * @param options `removeDefaults`: コード内既定訳も剥がすか (既定 `false`)
 */
export function normalizeLocalizedSsml(
  content: BroadcastContent,
  { removeDefaults = false }: { removeDefaults?: boolean } = {},
): BroadcastContent {
  if (content.category === 'self_train_delay') {
    const props = content.properties as SelfTrainDelayProperties;
    if (!Array.isArray(props.delay_patterns)) return content;
    return {
      ...content,
      properties: {
        ...props,
        delay_patterns: prunePatterns(props.delay_patterns, removeDefaults) ?? [],
      },
    };
  }
  if (content.category === 'connecting_line_delay') {
    const props = content.properties as ConnectingLineDelayProperties;
    const mode = props.delay_judgement_mode ?? 'whole_line';
    return {
      ...content,
      properties:
        // 遅延パターン表で設定するモード (transfer_aware / departure_forecast)。
        // ※ `utils/broadcastContents.ts` の `usesDelayPatternsTable` と同じ規則
        //   (循環 import を避けるためここでは inline で判定する)。
        mode === 'transfer_aware' || mode === 'departure_forecast'
          ? {
              ...props,
              ...(Array.isArray(props.delay_patterns)
                ? { delay_patterns: prunePatterns(props.delay_patterns, removeDefaults) }
                : {}),
            }
          : withPrunedField(props, 'ssml_template_i18n', removeDefaults),
    };
  }
  if (content.category === 'connecting_line_delay_by_station') {
    const props = content.properties as ConnectingLineDelayByStationProperties;
    if (props.transfer_stop_mode === 'other') return content;
    const nextProps = withPrunedField(props, 'ssml_intro_template_i18n', removeDefaults);
    const nextPropsWithOverLimit = withPrunedField(
      nextProps,
      'over_limit_ssml_template_i18n',
      removeDefaults,
    );
    return {
      ...content,
      properties: {
        ...nextPropsWithOverLimit,
        ...(Array.isArray(props.lines)
          ? {
              lines: props.lines.map((line) => {
                const nextLine = withPrunedField(line, 'ssml_line_template_i18n', removeDefaults);
                if (!Array.isArray(line.delay_patterns)) return nextLine;
                return {
                  ...nextLine,
                  delay_patterns: prunePatterns(line.delay_patterns, removeDefaults),
                };
              }),
            }
          : {}),
      },
    };
  }
  return content;
}

/**
 * 既存データに焼き込まれたコード内既定訳を剥がす (Issue #556 / 読み込み時の移行)。
 *
 * 放送地点を **開いた時点** で「空欄 (= 定型文マスタ委譲)」へ戻すため、
 * そのまま保存すれば以後マスタの多言語編集が反映される。
 */
export function unbakeLocalizedSsmlDefaults(content: BroadcastContent): BroadcastContent {
  return normalizeLocalizedSsml(content, { removeDefaults: true });
}
